// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { generateSdkPassword } from './sdk-password';
import React, { ReactNode } from 'react';
import { ExternalLink } from '../commons';
import { ToolsContent, WizardState } from './interfaces';

interface Option {
  label: string;
  value: string;
  description?: string;
  tags?: string[];
  labelTag?: string;
  resource_value?: string
}

interface OptionGroup {
  label: string;
  options: Option[];
}

export const FLINK_VERSION: OptionGroup[] = [
  {
    label: '',
    options: [
      // Must match the Flink of the job-server jar in every app's -build image
      // (noeesis/applications base/flink-images and apps/*/docker/Dockerfile.build).
      // 1.19, not 1.18: 1.18.1 has FLINK-34007, a Kubernetes HA leader-election bug,
      // and Beam 2.65 supports Flink up to 1.19. The operator derives the Flink
      // operator's flinkVersion from this image's tag.
      {
        value: '2',
        label: '1.19.3',
        resource_value: '035611742039.dkr.ecr.eu-west-2.amazonaws.com/flink:1.19.3-2.65.0',
      },
    ],
  },
];

export const INSTANCE_SIZE_OPTIONS: OptionGroup[] = [
  {
    label: '',
    options: [
      { value: '1', label: 'Large' },
      { value: '2', label: 'xLarge' },
      { value: '3', label: '2xLarge' },
      { value: '4', label: '4xLarge' },
      { value: '4', label: '8xlarge' },
      { value: '4', label: '12xlarge' },
      { value: '4', label: '16xlarge' },
      { value: '4', label: '24xlarge' },
      { value: '4', label: '32xlarge' },
    ],
  },
];

export const CLASS_OPTIONS: OptionGroup[] = [
  {
    label: 'Current generation burstable performance instance classes',
    options: [
      { value: '1', label: 'c6in.large', resource_value: 'large', tags: ['2 vCPU', '4 GiB RAM'], },
      { value: '2', label: 'c6in.xlarge', resource_value: 'xlarge', tags: ['4 vCPU', '8 GiB RAM'] },
      { value: '3', label: 'c6in.2xlarge', resource_value: '2xlarge', tags: ['8 vCPU', '16 GiB RAM'] },
      { value: '4', label: 'c6in.4xlarge', resource_value: '4xlarge', tags: ['16 vCPU', '32 GiB RAM'] },
      { value: '5', label: 'c6in.8xlarge', resource_value: '8xlarge', tags: ['32 vCPU', '64 GiB RAM'] },
    ],
  },
  {
    label: 'Latest generation standard instance classes',
    options: [
      { value: '6', label: 'm4.large', tags: ['2 vCPU', '8 GiB RAM'] },
      { value: '7', label: 'm4.xlarge', tags: ['4 vCPU', '16 GiB RAM'] },
      { value: '8', label: 'm4.2xlarge', tags: ['8 vCPU', '32 GiB RAM'] },
      { value: '9', label: 'm4.4xlarge', tags: ['16 vCPU', '64 GiB RAM'] },
    ],
  },
  {
    label: 'Current generation memory optimized instance classes',
    options: [
      { value: '10', label: 'r4.large', tags: ['2 vCPU', '15.25 GiB RAM'] },
      { value: '11', label: 'r4.xlarge', tags: ['4 vCPU', '30.5 GiB RAM'] },
      { value: '12', label: 'r4.2xlarge', tags: ['8 vCPU', '61 GiB RAM'] },
    ],
  },
];

// resource_value maps to the karpenter.sh/capacity-type label value actually
// used by this platform's NodePools (platform/resources/components/karpenter/
// config/base/nodepool/cpu-spot.yaml, cpu-on-demand.yaml).
export const NODEPOOL_OPTIONS: OptionGroup[] = [
  {
    label: 'CPU',
    options: [
      { value: '1', label: 'Spot', resource_value: 'spot', tags: ['Includes c series, intel and arm architecture'] },
      { value: '2', label: 'On-Demand', resource_value: 'on-demand', tags: ['Includes c series, intel and arm architecture'] },
    ],
  },
  {
    label: 'Memory',
    options: [
      { value: '3', label: 'Spot', resource_value: 'spot', tags: ['Includes c series, intel and arm architecture'] },
      { value: '4', label: 'On-Demand', resource_value: 'on-demand', tags: ['Includes c series, intel and arm architecture'] },
    ],
  },
  {
    label: 'General Purpose',
    options: [
      { value: '5', label: 'Spot', resource_value: 'spot', tags: ['Includes c series, intel and arm architecture'] },
      { value: '6', label: 'On-Demand', resource_value: 'on-demand', tags: ['Includes c series, intel and arm architecture'] },
    ],
  },
];

export const STORAGE_TYPES: Option[] = [
  {
    value: 'General Purpose (SSD)',
    label: 'General Purpose (SSD)',
    description:
      'Suitable for a broad range of database workloads. Provides baseline of 3 IOPS/GiB and ability to burst to 3,000 IOPS.',
  },
  {
    value: 'Provisioned IOPS (SSD)',
    label: 'Provisioned IOPS (SSD)',
    description:
      'Suitable for I/O-intensive database workloads. Provides flexibility to provision I/O ranging from 1,000 to 20,000 IOPS.',
  },
  {
    value: 'Magnetic',
    label: 'Magnetic',
    description:
      'Suitable for backward compatibility. The maximum amount of storage allowed for DB instances on magnetic storage is less than that of the other storage types.',
  },
];

export const TIME_ZONES: Option[] = [
  {
    label: 'No preference',
    value: '1',
  },
  {
    label: 'AUS Central Standard Time',
    value: '2',
  },
  {
    label: 'AUS Eastern Standard Time',
    value: '3',
  },
  {
    label: 'Afghanistan Standard Time',
    value: '4',
  },
  {
    label: 'Alaskan Standard Time',
    value: '5',
  },
  {
    label: 'Atlantic Standard Time',
    value: '6',
  },
  {
    label: 'Belarus Standard Time',
    value: '7',
  },
  {
    label: 'Cananda Central Standard Time',
    value: '8',
  },
  {
    label: 'Cananda Eastern Standard Time',
    value: '9',
  },
];

// resource_value is the deliberate "skip" sentinel for 'none' -- left empty
// so the renderer omits the nodeSelector zone key entirely rather than
// writing a blank/wrong value into it.
export const AVAILABILITY_ZONES: Option[] = [
  { value: 'none', label: 'No preference', resource_value: '' },
  { value: 'a', label: 'us-east-1a', resource_value: 'us-east-1a' },
  { value: 'c', label: 'us-east-1c', resource_value: 'us-east-1c' },
  { value: 'd', label: 'us-east-1d', resource_value: 'us-east-1d' },
  { value: 'e', label: 'us-east-1e', resource_value: 'us-east-1e' },
];

export const ENVIRONMENTS: Option[] = [
  {
    label: 'Development',
    value: 'dev',
  },
  {
    label: 'Staging',
    value: 'stage',
  },
  {
    label: 'Production',
    value: 'prod',
  },
];

export const CHART_VERSION: OptionGroup[] = [
  {
    label: '',
    options: [
      // Must match a real tag on noeesis/charts. This value is written into
      // application state and OVERRIDES streaming.go's own default, so a stale
      // entry here is not cosmetic -- it makes the chart GitRepository
      // unresolvable ("couldn't find remote ref refs/tags/..."). Verified 2026-10-08.
      //
      // 2.x only: 2.0.0 runs the app as an Apache Flink Kubernetes Operator
      // FlinkDeployment. The 1.0.x charts rendered the retired Spotify operator's
      // FlinkCluster; the operator now maps a 1.x pin in an existing app's state
      // to its 2.x default.
      { value: '3', label: 'streaming-chart-2.0.0', resource_value: 'streaming-chart-2.0.0' },
    ],
  },
];

export const CHECKPOINT_MODE_OPTIONS: OptionGroup[] = [
  {
    label: '',
    options: [
      { value: 'EXACTLY_ONCE', label: 'Exactly once', resource_value: 'EXACTLY_ONCE' },
      { value: 'AT_LEAST_ONCE', label: 'At least once', resource_value: 'AT_LEAST_ONCE' },
    ],
  },
];

export const DEFAULT_STEP_INFO: WizardState = {
  application: {
    applicationName: "application-name",
    organisationName: "organisation-name",
    environment: ENVIRONMENTS[0],
    flink_version: FLINK_VERSION[0].options[0],
    streaming_chart_version: CHART_VERSION[0].options[0],
    password: generateSdkPassword(),
  },
  details:
  {
    taskManager_resources_limits_cpu: "1",
    taskManager_resources_limits_memory: "4",
    taskManager_resources_requests_memory: "3",
    taskManager_resources_requests_cpu: "1",
    taskManager_number_of_task_slots: "2",
    taskManager_scale_average_utilization: "50",
    taskManager_scale_max_Replicas: "4",
    taskManager_scale_min_replicas: "1",
    taskManager_scale_enabled: "false",
    taskManager_availability_zone: AVAILABILITY_ZONES[0],
    taskManager_nodepool: NODEPOOL_OPTIONS[0].options[0],
    taskManager_instance_class: CLASS_OPTIONS[0].options[0],
    taskManager_instance_size: CLASS_OPTIONS[0].options[0],
    jobManager_resources_requests_cpu: "1",
    jobManager_resources_limits_memory: "3",
    jobManager_resources_limits_cpu: "2",
    jobManager_resources_requests_memory: "1",
    jobManager_availability_zone: AVAILABILITY_ZONES[0],
    jobManager_nodepool: NODEPOOL_OPTIONS[0].options[0],
    jobManager_instance_class: CLASS_OPTIONS[0].options[0],
    jobManager_instance_size: CLASS_OPTIONS[0].options[0],
    storage_access_mode: '',
    storage_class_name: 'platform-efs',
    checkpoint_enabled: "false",
    checkpoint_s3_dir: "",
    savepoint_s3_dir: "",
    checkpoint_interval_ms: "300000",
    checkpoint_timeout_ms: "3600000",
    checkpoint_mode: CHECKPOINT_MODE_OPTIONS[0].options[0],
  },
  deployment:
  {
    containers_name: "",
    containers_image: "",
    containers_tag: "",
    resources_requests_memory: "1",
    resources_requests_cpu: "1",
    resources_limits_memory: "2",
    resources_limits_cpu: "2",
    parallelism: "2",
    number_of_workers: "4",
    entry_point: "ncoda_core.main",
    storage_class_name: "platform-efs",
  },
  kafka:
  {
    producer_spec_config_retention_ms: "604800000",
    // Each app gets a single-broker Kafka cluster, so a replication factor above
    // 1 can never be satisfied -- "2" here made every topic fail to create. The
    // operator now caps it at the broker count regardless; this default just
    // stops the UI from asking for something impossible.
    producer_spec_replicas: "1",
    producer_spec_partitions: "1",
    producer_spec_segment_bytes: "1073741824",
    producer_topic: "topic-out",
    consumer_spec_config_retention_ms: "604800000",
    consumer_spec_replicas: "1",
    consumer_spec_partitions: "1",
    consumer_spec_segment_bytes: "1073741824",
    consumer_topic: "topic-in",
    consumer_topic_mode: "new",
    producer_topic_mode: "new"
  },
  review: undefined,
};

export const TOOLS_CONTENT: Record<string, Record<string, ToolsContent>> = {
  application: {
    default: {
      title: 'Select engine type',
      content: (
        <div>
          <p>
            With Amazon RDS, you can create a <i>DB instance</i>, an isolated database environment in the AWS Cloud. A
            DB instance can contain multiple user-created databases. You can access your instance by using the same
            tools and applications that you might use with a standalone database instance.
          </p>
          <p>
            <b>Note</b>
            <br />
            Before you can create a DB instance, you must complete the tasks in{' '}
            <ExternalLink href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_SettingUp.html">
              Setting up for Amazon RDS
            </ExternalLink>
            .
          </p>
          <p>
            Each DB instance runs a <i>DB engine</i>. Amazon RDS supports several engine types: Amazon Aurora, MySQL,
            MariaDB, PostgreSQL, Oracle, and Microsoft SQL Server. Each DB engine has its own supported features, and
            each version of a DB engine might include specific features. Additionally, each DB engine has a set of
            parameters in a DB parameter group that control the behavior of the databases that it manages.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Welcome.html',
          text: 'What is Amazon RDS?',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_GettingStarted.html',
          text: 'Getting started with Amazon RDS',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.DBInstance.html',
          text: 'Amazon RDS database instances',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/CHAP_AuroraOverview.html',
          text: 'What is Amazon Aurora?',
        },
      ],
    },
  },
  details: {
    default: {
      title: 'Specify instance details 123',
      content: (
        <p>
          After you select an engine for your DB instance, you're ready to specify the details for the instance, such as
          the instance class and storage type.
        </p>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.DBInstance.html',
          text: 'Amazon RDS database instances',
        },
      ],
    },
    instanceClass: {
      title: 'Class',
      content: (
        <div>
          <p>
            Amazon RDS supports three types of instance classes: Standard, Memory Optimized, and Burstable Performance.
          </p>
          <h3>Standard</h3>
          <p>
            Provides a balance of compute, memory, and network resources. This class is a good choice for many
            applications.
          </p>
          <h3>Memory Optimized</h3>
          <p>Designed for memory-intensive applications. This class offers a low price per GiB of RAM.</p>
          <h3>Burstable Performance</h3>
          <p>Provides a baseline performance level, with the ability to burst to full CPU usage.</p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Concepts.DBInstanceClass.html',
          text: 'Choosing the DB instance class',
        },
      ],
    },
    storageType: {
      title: 'Storage type',
      content: (
        <div>
          <p>
            When you launch an instance, the instance class that you specify determines the hardware of the host
            computer used for your instance. Each instance class offers different compute, memory, and storage
            capabilities. Choose an instance class based on the requirements of the application or software that you
            plan to run on your instance. Amazon RDS supports three types of instance classes:
          </p>
          <ul>
            <li>
              <b>General Purpose SSD</b> – General Purpose SSD volumes offer cost-effective storage that is ideal for a
              broad range of workloads. These volumes deliver single-digit millisecond latencies and the ability to
              burst to 3,000 IOPS for extended periods of time. Baseline performance for these volumes is determined by
              the volume's size.
            </li>
            <li>
              <b>Provisioned IOPS SSD</b> – Provisioned IOPS storage is designed to meet the needs of I/O-intensive
              workloads, particularly database workloads, that require low I/O latency and consistent I/O throughput.
            </li>
            <li>
              <b>Magnetic</b> – Amazon RDS also supports magnetic storage for backward compatibility. We recommend that
              you use General Purpose SSD or Provisioned IOPS for any new storage needs. The maximum amount of storage
              allowed for DB instances on magnetic storage is less than that of the other storage types.
            </li>
          </ul>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_Storage.html#Concepts.Storage.GeneralSSD',
          text: 'General Purpose SSD storage',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_Storage.html#USER_PIOPS',
          text: 'Provisioned IOPS SSD storage',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_Storage.html#CHAP_Storage.Magnetic',
          text: 'Magnetic storage',
        },
      ],
    },
    iamAuth: {
      title: 'IAM DB authentication',
      content: (
        <div>
          <p>
            With IAM database authentication, you use an <i>authentication token</i> when you connect to your DB
            instance. An authentication token is a string of characters that you use instead of a password. After you
            generate an authentication token, it's valid for 15 minutes before it expires. If you try to connect using
            an expired token, the connection request is denied.
          </p>

          <p>
            Every authentication token must be accompanied by a valid signature, using AWS signature version 4. You can
            use an authentication token when you connect to Amazon RDS from another AWS service, such as AWS Lambda. By
            using a token, you can avoid placing a password in your code.
          </p>
          <p>After you have a signed IAM authentication token, you can connect to an Amazon RDS DB instance.</p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/UsingWithRDS.IAMDBAuth.Enabling.html',
          text: 'Enabling and Disabling IAM Database Authentication',
        },
        {
          href: 'https://docs.aws.amazon.com/general/latest/gr/signature-version-4.html',
          text: 'Signature Version 4 Signing Process',
        },
      ],
    },
    identifier: {
      title: 'Node Class',
      content: (
        <div>
          <p>
            Select the instance type, size, class.  Decide on spot or on-demand.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.DBInstance.html',
          text: 'Amazon RDS database instances',
        },
      ],
    },
    username: {
      title: 'Primary user name',
      content: (
        <div>
          <p>
            As part of the creation process, Amazon RDS creates a <i>primary user</i> for your DB instance. This primary
            user has permissions to create databases and to perform create, delete, select, update, and insert
            operations on tables that the primary user creates.
          </p>
          <p>
            Specify an alphanumeric string that defines the login ID (the name) for the primary user. The name must
            start with a letter, as in "awsuser".
          </p>
          <p>
            <b>Important</b> <br />
            We strongly recommend that you don't use the primary user credentials directly in your applications.
            Instead, follow the best practice of using a database user created with the minimal privileges required for
            your application.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/UsingWithRDS.MasterAccounts.html',
          text: 'Primary user account privileges',
        },
      ],
    },
    password: {
      title: 'SDK password',
      links: [],
      content: (
        <div>
          <p>
            The SCRAM credential for this app's own Kafka user. A strong value is generated for you, so you normally
            leave it alone.
          </p>
          <p>
            The platform writes it into the Secret the Kafka user is created from and into the SDK configuration the
            pipeline reads, so it never needs to be typed anywhere else. Changing it here rotates the credential: the
            Kafka user and the running pipeline both pick up the new value.
          </p>
          <p>
            <b>Leaving it empty breaks the app.</b> Kafka refuses a user with no password, the user is never created,
            and the pipeline cannot connect.
          </p>
        </div>
      ),
    },
  },
  advanced: {
    default: {
      title: 'Configure settings',
      content: (
        <p>
          Now you are ready to configure your settings for your DB instance. You can choose to run your DB instance in a
          VPC, enable encryption, and set up monitoring.
        </p>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_GettingStarted.html',
          text: 'Getting started with Amazon RDS',
        },
      ],
    },
    vpc: {
      title: 'Virtual Private Cloud (VPC)',
      content: (
        <div>
          <p>
            You can choose to launch your DB instance into a VPC that you've created with Amazon Virtual Private Cloud
            (Amazon VPC). The dropdown list shows your existing VPCs that have a corresponding DB subnet group.
          </p>
          <p>
            If you want to use a new VPC instead of an existing one, you can open the Amazon VPC console, create a VPC,
            and then return to this page. Your new VPC should be listed and available for you to choose.
          </p>
          <h3>Benefits</h3>
          <p>
            Amazon VPC lets you launch AWS resources, such as Amazon RDS DB instances, into a VPC. A virtual private
            cloud (VPC) is a virtual network dedicated to your AWS account. It is logically isolated from other virtual
            networks in the AWS Cloud.
          </p>
          <p>
            When you use a VPC, you have control over your virtual networking environment. You can choose your own IP
            address range, create subnets, and configure routing and access control lists. The basic functionality of
            Amazon RDS is the same whether your DB instance is running in a VPC or not: Amazon RDS manages backups,
            software patching, automatic failure detection, and recovery. There is no additional cost to run your DB
            instance in a VPC.
          </p>
          <p>All new DB instances are created in a default VPC unless you specify otherwise.</p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_VPC.html',
          text: 'Amazon VPC and Amazon RDS',
        },
      ],
    },
    subnet: {
      title: 'Subnet group',
      content: (
        <div>
          <p>
            A <i>subnet</i> is a segment of a VPC's IP address range that you designate to group your resources based on
            security and operational needs. A DB <i>subnet group</i> is a collection of subnets (typically private) that
            you create in a VPC and that you then designate for your DB instances.
          </p>
          <p>
            Each DB subnet group must have subnets in at least two Availability Zones in a specified AWS Region. If you
            have a default VPC, the service automatically creates a subnet for you in each Availability Zone in the
            Region.
          </p>
          <p>
            When creating a DB instance in a VPC, you must choose a DB subnet group. Amazon RDS uses that DB subnet
            group and your preferred Availability Zone to select a subnet and an IP address within that subnet to
            associate with your DB instance. If the primary DB instance of a Multi-AZ deployment fails, Amazon RDS can
            promote the corresponding standby and subsequently create a new standby using an IP address of the subnet in
            one of the other Availability Zones.
          </p>
          <p>
            <b>Note</b>
            <br />
            For each DB instance that you run in a VPC, you should reserve at least one IP address in each subnet in the
            DB subnet group for use by Amazon RDS for recovery actions.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/USER_VPC.WorkingWithRDSInstanceinaVPC.html',
          text: 'Working with an Amazon RDS DB instance in a VPC',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/CHAP_Tutorials.WebServerDB.CreateVPC.html',
          text: 'Tutorial: Create an Amazon VPC for use with an Amazon RDS DB instance',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/USER_VPC.Scenarios.html',
          text: 'Scenarios for accessing a DB instance in a VPC',
        },
      ],
    },
    securityGroups: {
      title: 'VPC security groups',
      content: (
        <div>
          <p>
            A <i>VPC security group</i> controls access to DB instances (and EC2 instances) inside a VPC. The security
            groups acts as a firewall for the associated DB instance, controlling both inbound and outbound traffic at
            the instance level. By default, the service creates a DB instance with a firewall and a security group that
            protect the DB instance.
          </p>
          <p>
            VPC security groups can have rules that govern both inbound and outbound traffic, although the outbound
            traffic rules typically don't apply to DB instances. Outbound traffic rules apply only if the DB instance
            acts as a client. For example, outbound traffic rules apply to an Oracle DB instance with outbound database
            links.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html',
          text: 'Security groups',
        },
      ],
    },
    accessibility: {
      title: 'Public accessibility',
      content: (
        <div>
          <p>
            If you enable a public connection, EC2 instances and devices outside of the VPC that host the DB instance
            can connect to the DB instance. If you don't enable a public connection, Amazon RDS doesn't assign a public
            IP address to the DB instance, and no EC2 instance or devices outside of the VPC can connect.
          </p>
          <p>
            If you enable a public connection, you must choose one or more VPC security groups that specify which EC2
            instances and devices can connect to the DB instance.
          </p>
          <p>
            <b>Note</b>
            <br />
            For a DB instance to be publicly accessible, the subnets in the DB subnet group must have an internet
            gateway.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_VPC.html#USER_VPC.Hiding',
          text: 'Amazon VPC and Amazon RDS',
        },
      ],
    },
    encryption: {
      title: 'Encryption',
      content: (
        <div>
          <p>
            You can encrypt your Amazon RDS DB instances and snapshots at rest by enabling the encryption option. Data
            that is encrypted at rest includes the underlying storage for a DB instance and its automated backups, read
            replicas, and snapshots.
          </p>
          <p>
            Amazon RDS encrypted DB instances use the industry standard AES-256 encryption algorithm to encrypt your
            data on the server that hosts your instances. After your data is encrypted, Amazon RDS handles
            authentication of access and decryption of your data transparently, with a minimal effect on performance.
            You don't need to modify your database client applications to use encryption.
          </p>
          <p>
            <b>Note</b>
            <br />
            For encrypted and unencrypted DB instances with cross-Region read replicas, data that is in transit between
            the source and the read replicas is encrypted.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.Encryption.html',
          text: 'Encrypting Amazon RDS resources',
        },
      ],
    },
    monitoring: {
      title: 'Enhanced monitoring',
      content: (
        <div>
          <p>Amazon RDS provides metrics in real time for the operating system (OS) that your DB instance runs on.</p>
          <p>The cost for using enhanced monitoring depends on several factors:</p>
          <ul>
            <li>
              You are charged only for enhanced monitoring that exceeds the free tier provided by Amazon CloudWatch
              Logs.
            </li>
            <li>
              A smaller monitoring interval results in more frequent reporting of OS metrics and increases your
              monitoring cost.
            </li>
            <li>
              Usage costs for enhanced monitoring are applied for each DB instance that enhanced monitoring is enabled
              for. Monitoring a large number of DB instances is more expensive than monitoring only a few.
            </li>
            <li>
              DB instances that support a more compute-intensive workload have more OS process activity to report and
              higher costs for enhanced monitoring.
            </li>
          </ul>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.html',
          text: 'Enhanced monitoring',
        },
        {
          href: 'https://aws.amazon.com/cloudwatch/pricing/',
          text: 'Amazon CloudWatch pricing',
        },
      ],
    },
    failover: {
      title: 'Failover priority',
      content: (
        <div>
          <p>
            In the event of a planned or unplanned outage of your DB instance, Amazon RDS automatically switches to a
            standby replica in another Availability Zone. Each read replica is associated with a priority tier (0-3).
            Amazon RDS promotes the read replica that has the highest priority (the lowest numbered tier). If two or
            more replicas have the same priority, RDS promotes the one that is the same size as the previous primary
            instance.{' '}
          </p>
          <p>
            The primary DB instance switches over automatically to the standby replica if any of the following
            conditions occur:
          </p>
          <ul>
            <li>An Availability Zone outage</li>
            <li>The primary DB instance fails</li>
            <li>The DB instance's server type is changed</li>
            <li>The operating system of the DB instance is undergoing software patching</li>
            <li>
              A manual failover of the DB instance was initiated using <b>Reboot with failover</b>
            </li>
          </ul>
          <p>
            The failover mechanism automatically changes the DNS record of the DB instance to point to the standby DB
            instance. As a result, you need to re-establish any existing connections to your DB instance.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Concepts.MultiAZ.html',
          text: 'High availability for Amazon RDS',
        },
      ],
    },
    backtrack: {
      title: 'Backtrack',
      content: (
        <div>
          <p>
            <i>Backtracking</i> lets you quickly recover from a user error. Backtracking "rewinds" the DB instance to a
            time that you specify. For example, if you accidentally delete an important record at 10:00 AM, you could
            use backtracking to move a database back to its state at 9:59 AM, before the error.
          </p>
          <p>
            Backtracking is not a replacement for backing up your DB instance so that you can restore it to a point in
            time. However, backtracking provides the following advantages over traditional backup and restore:
          </p>
          <ul>
            <li>
              You can easily undo mistakes. If you mistakenly perform a destructive action, such as a DELETE without a
              WHERE clause, you can backtrack the DB instance to a time before the destructive action, with minimal
              interruption of service.
            </li>
            <li>
              You can backtrack a DB instance quickly. Restoring a DB instance to a point in time launches a new DB
              instance and restores it from backup data or a DB instance snapshot, which can take hours. Backtracking a
              DB instance doesn't require a new DB instance and rewinds the DB instance in minutes.
            </li>
            <li>
              You can explore earlier data changes. You can repeatedly backtrack a DB instance back and forth in time to
              help determine when a particular data change occurred. For example, you can backtrack a DB instance three
              hours and then backtrack forward in time one hour. In this case, the backtrack time is two hours before
              the original time.
            </li>
          </ul>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_PIT.html',
          text: 'Restoring a DB instance to a specified time',
        },
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/AuroraMySQL.Managing.Backtrack.html',
          text: 'Backtracking an Aurora DB cluster',
        },
      ],
    },
  },
  review: {
    default: {
      title: 'Review and create',
      content: (
        <div>
          <p>
            Verify your settings for your DB instance, and edit the settings as needed. When you are satisfied with your
            settings, choose <b>Create DB instance</b>.
          </p>
          <p>
            After Amazon RDS creates your instance, the new instance appears in the list of DB instances on the console.
            The DB instance has a status of <b>Creating</b> until the instance is ready to use. When the state changes
            to <b>Available</b>, you can connect to the instance.
          </p>
          <p>
            Depending on the instance class and the amount of storage, it can take up to 20 minutes before the new
            instance is available.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.DBInstance.html',
          text: 'Amazon RDS DB instances',
        },
      ],
    },
  },
  jobmanagerdetails: {},
  taskmanagerdetails: {},
  deployment: {
    default: {
      title: 'Review and create',
      content: (
        <div>
          <p>
            Verify your settings.
          </p>
          <p>
            More info here.
          </p>
          <p>
            It can take up to 20 minutes before the new
            instance is available.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://ncoda.ai',
          text: 'link here',
        },
      ],
    },
  },
  kafka: {
    default: {
      title: 'Review and create',
      content: (
        <div>
          <p>
            Verify your settings.
          </p>
          <p>
            More info here.
          </p>
          <p>
            It can take up to 20 minutes before the new
            instance is available.
          </p>
        </div>
      ),

      links: [
        {
          href: 'https://ncoda.ai',
          text: 'link here',
        },
      ],
    },
  },
};
